import type { CadenceStepItem } from '../CGStep/stepData';
import type { CadenceStepTypePure } from '../CGStep/types';
import {
  callStep,
  connectStep,
  emailStep,
  followStep,
  inmailStep,
  likeRecentPostsStep,
  messageStep,
  reminderStep,
  switchCampaignStep,
  switchStep,
  voiceNoteStep,
} from '../CGStep/stepData';

/**
 * Step types allowed to be added inside a single day block on the contact
 * detail campaign view. `visit` is excluded. `call` is allowed but gated by the
 * team's voice-calling flag at the picker layer (see CampaignStepPickerPure /
 * TemplateStepPickerPure), the same way `voice_note` is gated by `voiceEnabled`.
 */
export const BLOCK_ALLOWED_STEP_TYPES: ReadonlySet<CadenceStepTypePure> = new Set<CadenceStepTypePure>([
  'connect',
  'message',
  'inmail',
  'likeRecentPosts',
  'follow',
  'email',
  'reminder',
  'call',
  'voice_note',
  // Only surfaced by pickers that opt in via `allowSwitchCampaign` (the Touch Campaign
  // Graph). Filtered out of the classic day-block picker so it never leaks there.
  'switch_campaign',
  // Likewise opt-in via `allowSwitch` — the numeric-range branch node (Touch Campaign Graph only).
  'switch',
]);

/**
 * Types that can appear at most once per block. Anything in
 * BLOCK_ALLOWED_STEP_TYPES not in this set is unlimited.
 *
 * Unlimited: message, inmail, email, reminder.
 */
export const BLOCK_ONCE_ONLY_STEP_TYPES: ReadonlySet<CadenceStepTypePure> = new Set<CadenceStepTypePure>([
  'connect',
  'likeRecentPosts',
  'follow',
  // At most one "Change to campaign" per block — it's a terminal leaf; a second is meaningless.
  'switch_campaign',
  // At most one "Switch" per block — a decision node owns all 6 arms; a second can't fit.
  'switch',
]);

/** Picker order for the day-block add menu. */
const BLOCK_STEP_ITEMS: readonly CadenceStepItem[] = [
  connectStep,
  messageStep,
  inmailStep,
  likeRecentPostsStep,
  followStep,
  emailStep,
  reminderStep,
  callStep,
  voiceNoteStep,
  switchCampaignStep,
  switchStep,
];

/**
 * Given the types already present in a day block, return the picker items
 * the user is still allowed to add. Once-only types disappear once present;
 * disallowed types (visit, call, etc.) are never returned.
 */
export function getBlockAddableStepItems(
  existingTypes: readonly CadenceStepTypePure[],
  allowSwitchCampaign = false,
  allowSwitch = false,
): CadenceStepItem[] {
  const present = new Set(existingTypes);
  return BLOCK_STEP_ITEMS.filter((item) => {
    if (!BLOCK_ALLOWED_STEP_TYPES.has(item.value)) return false;
    // switch_campaign is opt-in (Touch Campaign Graph only) — excluded everywhere else.
    if (item.value === 'switch_campaign' && !allowSwitchCampaign) return false;
    // switch (numeric-range branch) is likewise opt-in.
    if (item.value === 'switch' && !allowSwitch) return false;
    if (BLOCK_ONCE_ONLY_STEP_TYPES.has(item.value) && present.has(item.value)) return false;
    return true;
  });
}

export type BlockStepItemStatus = {
  item: CadenceStepItem;
  disabled: boolean;
  /** Human-readable reason for the disabled state, suitable for tooltips. */
  disabledReason?: string;
};

/**
 * Like getBlockAddableStepItems, but always returns every block-allowed type.
 * Once-only types that are already present are returned with `disabled: true`
 * so the picker can render them grayed out with an explanatory tooltip.
 *
 * `extraDisabled` — host-supplied per-type disabled reasons (e.g. "you are
 * already connected to this contact" for `connect`). Takes precedence over
 * the once-only check so the host's reason wins when both apply.
 *
 * `restrictToType` — touch campaigns lock a template to a single step type. When
 * set, only that type is returned (the picker offers nothing else).
 *
 * `allowSwitchCampaign` — opt-in for the `switch_campaign` step. Only the Touch
 * Campaign Graph passes true; every other picker leaves it excluded.
 *
 * `allowSwitch` — opt-in for the `switch` (numeric-range branch) step, same deal.
 *
 * Disallowed types (visit, call, etc.) are still excluded entirely.
 */
export function getBlockStepItemsWithStatus(
  existingTypes: readonly CadenceStepTypePure[],
  extraDisabled?: Partial<Record<CadenceStepTypePure, string>>,
  restrictToType?: CadenceStepTypePure | null,
  allowSwitchCampaign = false,
  allowSwitch = false,
): BlockStepItemStatus[] {
  const present = new Set(existingTypes);
  return BLOCK_STEP_ITEMS.filter(
    (item) =>
      BLOCK_ALLOWED_STEP_TYPES.has(item.value) &&
      (item.value !== 'switch_campaign' || allowSwitchCampaign) &&
      (item.value !== 'switch' || allowSwitch) &&
      (restrictToType == null || item.value === restrictToType),
  ).map((item) => {
    const externalReason = extraDisabled?.[item.value];
    if (externalReason != null) {
      return { item, disabled: true, disabledReason: externalReason };
    }
    if (BLOCK_ONCE_ONLY_STEP_TYPES.has(item.value) && present.has(item.value)) {
      return {
        item,
        disabled: true,
        disabledReason: `Only one ${item.label} step is allowed per day. Remove the existing one to add another.`,
      };
    }
    return { item, disabled: false };
  });
}

/**
 * Convenience predicate: can ANY type still be added to a block with these
 * existing types? Used to disable the add button when nothing's left.
 * (With current rules this only returns false in pathological synthetic
 * cases, since message/inmail/email/reminder are unlimited.)
 */
export function canAddAnyStepToBlock(existingTypes: readonly CadenceStepTypePure[]): boolean {
  return getBlockAddableStepItems(existingTypes).length > 0;
}
